import { useState, useRef, useEffect, type ReactNode } from "react";

/**
 * Enterprise terminology glossary with definitions
 * Terms are explained in plain language for end users
 */
export const GLOSSARY: Record<
 string,
 { term: string; definition: string; example?: string }
> = {
 // Project Management Terms
 RACI: {
 term: "RACI Matrix",
 definition: "A chart showing who is Responsible, Accountable, Consulted, and Informed for each task.",
 example: "R = Does the work, A = Approves, C = Gives input, I = Kept updated",
 },
 milestone: {
 term: "Milestone",
 definition: "A significant checkpoint or goal in a project that marks important progress.",
 example: "Design Complete, Beta Release, Go Live",
 },
 worklog: {
 term: "Work Log",
 definition: "A record of time spent working on a specific task, including hours and notes.",
 example: "4 hours on bug fixes, 2 hours in meetings",
 },
 timesheet: {
 term: "Timesheet",
 definition: "A weekly summary of all your work hours across different projects and tasks.",
 },
 sprint: {
 term: "Sprint",
 definition: "A fixed time period (usually 2 weeks) where specific work is planned and completed.",
 },

  // Status Terms
  "in progress": {
 term: "In Progress",
 definition: "Work has started but is not yet complete.",
 },
 pending: {
 term: "Pending",
 definition: "Waiting for review or approval before it can proceed.",
 },
 approved: {
 term: "Approved",
 definition: "Reviewed and accepted by a manager or approver.",
 },
 rejected: {
 term: "Rejected",
 definition: "Not accepted - usually requires changes and resubmission.",
 },

  // Role Terms
  "project manager": {
    term: "Project Manager",
    definition: "The person responsible for planning, organizing, and overseeing a project.",
  },
  "team lead": {
    term: "Team Lead",
    definition: "A senior team member who guides and coordinates the work of other team members.",
  },
 assignee: {
 term: "Assignee",
 definition: "The person assigned to complete a specific task.",
 },
 reporter: {
 term: "Reporter",
 definition: "The person who created or reported an issue or task.",
 },

  // Time Terms
  "estimated hours": {
    term: "Estimated Hours",
    definition: "The predicted amount of time a task will take to complete.",
  },
  "actual hours": {
    term: "Actual Hours",
    definition: "The real time spent working on a task.",
  },
 billable: {
 term: "Billable Hours",
 definition: "Work hours that can be charged to a client.",
 },
 allocation: {
 term: "Allocation",
 definition: "How your work time is distributed across different projects.",
 example: "60% Project A, 40% Project B",
 },

 // Priority Terms
 critical: {
 term: "Critical Priority",
 definition: "Highest urgency - requires immediate attention.",
 },
 high: {
 term: "High Priority",
 definition: "Important and should be addressed soon.",
 },
 medium: {
 term: "Medium Priority",
 definition: "Normal importance - complete in regular workflow.",
 },
 low: {
 term: "Low Priority",
 definition: "Can be addressed when higher priorities are done.",
 },

 // Document Terms
 BRD: {
 term: "BRD (Business Requirements Document)",
 definition: "A document that describes what a project needs to accomplish from a business perspective.",
 },
 SRS: {
 term: "SRS (Software Requirements Specification)",
 definition: "A detailed description of how software should work and what features it should have.",
 },
};

/**
 * Find glossary entry by matching term (case-insensitive)
 */
export function findGlossaryEntry(text: string) {
 const normalized = text.toLowerCase().trim();
 return GLOSSARY[normalized] || null;
}

/**
 * GlossaryTooltip - Displays a helpful tooltip for enterprise terminology
 * Wrap any term that might need explanation
 */
export function GlossaryTooltip({
 term,
 children,
}: {
 term: string;
 children: ReactNode;
}) {
 const [isOpen, setIsOpen] = useState(false);
 const [position, setPosition] = useState<{
 top: number;
 left: number;
 } | null>(null);
 const triggerRef = useRef<HTMLSpanElement>(null);
 const tooltipRef = useRef<HTMLDivElement>(null);

 const entry = findGlossaryEntry(term);

 useEffect(() => {
 if (isOpen && triggerRef.current) {
 const rect = triggerRef.current.getBoundingClientRect();
 const tooltipWidth = 280;

 // Position tooltip above or below based on viewport space
 let top = rect.bottom + 8;
 let left = rect.left + rect.width / 2 - tooltipWidth / 2;

 // Adjust if too close to edges
 if (left < 8) left = 8;
 if (left + tooltipWidth > window.innerWidth - 8) {
 left = window.innerWidth - tooltipWidth - 8;
 }

 // If not enough space below, show above
 if (top + 150 > window.innerHeight) {
 top = rect.top - 8;
 }

 setPosition({ top, left });
 }
 }, [isOpen]);

 // Close on click outside
 useEffect(() => {
 if (!isOpen) return;

 const handleClick = (e: MouseEvent) => {
 if (
 tooltipRef.current &&
 !tooltipRef.current.contains(e.target as Node) &&
 triggerRef.current &&
 !triggerRef.current.contains(e.target as Node)
 ) {
 setIsOpen(false);
 }
 };

 document.addEventListener("click", handleClick);
 return () => document.removeEventListener("click", handleClick);
 }, [isOpen]);

 if (!entry) {
 return <>{children}</>;
 }

 return (
 <>
 <span
 ref={triggerRef}
 onClick={() => setIsOpen(!isOpen)}
 onMouseEnter={() => setIsOpen(true)}
 onMouseLeave={() => setIsOpen(false)}
 className="cursor-help border-b border-dashed border-slate-400 hover:border-role-primary hover:text-role-primary transition-colors"role="button"aria-describedby={isOpen ?"glossary-tooltip": undefined}
 >
 {children}
 </span>

 {isOpen && position && (
 <div
 ref={tooltipRef}
 id="glossary-tooltip"role="tooltip"className="fixed z-50 w-[280px] rounded-lg border border-slate-200 bg-white p-3 shadow-lg animate-in fade-in-0 zoom-in-95 duration-150"style={{ top: position.top, left: position.left }}
 >
 <div className="flex items-start gap-2">
 <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-50 text-role-primary">
 <svg
 className="h-3.5 w-3.5"fill="none"viewBox="0 0 24 24"stroke="currentColor"strokeWidth={2}
 >
 <path
 strokeLinecap="round"strokeLinejoin="round"d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
 </svg>
 </span>
 <div className="flex-1 min-w-0">
 <p className="text-sm font-semibold text-slate-900">
 {entry.term}
 </p>
 <p className="mt-1 text-xs text-slate-600">
 {entry.definition}
 </p>
 {entry.example && (
 <p className="mt-2 text-xs text-slate-500 italic">
 Example: {entry.example}
 </p>
 )}
 </div>
 </div>
 </div>
 )}
 </>
 );
}

/**
 * TermHighlight - Automatically detects and highlights glossary terms in text
 * Useful for descriptions and longer text blocks
 */
export function TermHighlight({ text }: { text: string }) {
 const terms = Object.keys(GLOSSARY);
 const regex = new RegExp(`\\b(${terms.join(" | ")})\\b`,"gi");

 const parts = text.split(regex);

 return (
 <>
 {parts.map((part, index) => {
 const entry = findGlossaryEntry(part);
 if (entry) {
 return (
 <GlossaryTooltip key={index} term={part}>
 {part}
 </GlossaryTooltip>
 );
 }
 return part;
 })}
 </>
 );
}

export default GlossaryTooltip;

